import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetById, Update } from "adminApi/RecruitmentApi/Recruitment";
import Spinner from "Common/Globle/Spinner";
import EditRecruitmentForm from "./components/EditRecruitmentForm";
import fields from "../json/Recruitment.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { RecruitmentRules } from "validation/ValidationRules";
import { validateArrayForm } from "utils/validation";

const EditRecruitment = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loadingData, setLoadingData] = useState(false);

    /**
     * Helper function to initialize form values from the fields array.
     */
    const buildInitialObject = (fieldList = []) =>
        fieldList.reduce((acc, field) => {
            acc[field.name] = "";
            return acc;
        }, {});

    /**
     * Initialize form values with default values.
     * supervisor_phone_country_dial_code lives inside each experience row,
     * NOT at the top level.
     */
    const initialValues = {
        ...buildInitialObject(fields.basic),
        phone_country_dial_code: "+91",
        alternate_phone_country_dial_code: "+91",
        education: [buildInitialObject(fields.education)],
        experience: [{ ...buildInitialObject(fields.experience), supervisor_phone_country_dial_code: "+91" }],
    };

    /**
     * useFormChange hook to handle form changes.
     */
    const {
        values,
        errors,
        handleChange,
        handleArrayChange,
        handleFileChange,
        setValues,
        setErrors,
    } = useFormChange(initialValues, RecruitmentRules);

    /**
     * Fetch candidate data by ID and populate form.
     * Ensures each experience row has supervisor_phone_country_dial_code.
     */
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            const result = await GetById(id);
            if (result.data?.success) {
                const data = result.data.data;

                const experience =
                    Array.isArray(data.experience) && data.experience.length
                        ? data.experience.map((row) => ({
                            supervisor_phone_country_dial_code: "+91",
                            ...row,
                        }))
                        : initialValues.experience;

                setValues({
                    ...initialValues,
                    ...data,
                    education:
                        Array.isArray(data.education) && data.education.length
                            ? data.education
                            : initialValues.education,
                    experience,
                });
            }
            setLoadingData(false);
        };
        if (id) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /**
     * useFormSubmit hook to handle form submission.
     */
    const { loading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([k, v]) => {
                if (Array.isArray(v)) {
                    formData.append(k, JSON.stringify(v));
                } else {
                    formData.append(k, v ?? "");
                }
            });
            return await Update({ id, data: formData });
        },
        (vals) => {
            const err = validateArrayForm(vals, RecruitmentRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Candidate updated successfully"
    );

    if (loadingData) return <Spinner isSpinner />;

    return (
        <>
            {loading && <Spinner isSpinner />}
            <EditRecruitmentForm
                fields={fields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                setValues={setValues}
                setErrors={setErrors}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                imagePreview={values.profile_picturePreview || values.image_url}
                title="Update Recruitment"
                handleArrayChange={handleArrayChange}
                rules={RecruitmentRules}
                buildInitialObject={buildInitialObject}
            />
        </>
    );
};

export default EditRecruitment;
